import React from "react";
import {
	Box,
	Button,
	Chip,
	Typography,
} from "@mui/material";
import {
	getCourierStatusLabel,
	getOrderStatusLabel,
} from "../../../utils/statusColors";
import { capitalizeFirstLetterOfEachWord } from "../../../utils/text";

const chipSx = { mr: 0.5, mb: 0.5, maxWidth: "100%" };

const formatTypeChipLabel = (typeLabel, values) => `${typeLabel}: ${values.join(", ")}`;

export const ActiveFilterChips = ({
	dateRangeLabel,
	onClearDate,
	warehouseNames = [],
	selectedWarehouseNames = [],
	onClearWarehouseNames,
	selectedWarehouseStatuses = [],
	onClearWarehouseStatuses,
	selectedCourierStatuses = [],
	onClearCourierStatuses,
	pastSLA = false,
	onClearPastSLA,
	onClearAll,
	clearAllDisabled = false,
	disabled = false,
}) => {
	const warehouseNameLabels = selectedWarehouseNames.map((id) => {
		const warehouse = warehouseNames.find((w) => String(w.id) === String(id));
		return warehouse?.name ?? String(id);
	});

	const warehouseStatusLabels = selectedWarehouseStatuses.map((status) => (
		capitalizeFirstLetterOfEachWord(getOrderStatusLabel(status))
	));

	const courierStatusLabels = selectedCourierStatuses.map((status) => (
		capitalizeFirstLetterOfEachWord(getCourierStatusLabel(status))
	));

	return (
		<Box
			className="active-filter-chips"
			sx={{
				display: "flex",
				alignItems: "center",
				flexWrap: "wrap",
				// Claim leftover row space, but never grow past it — chips wrap inside instead.
				flex: "1 1 0%",
				minWidth: 0,
				maxWidth: "100%",
				pl: 2,
				pr: 1,
				mb: 2,
				// Keep Filter / Search pinned; chips grow downward when they wrap.
				alignSelf: "flex-start",
				pt: 0.75,
			}}
		>
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ mr: 1, whiteSpace: "nowrap" }}
			>
				Active filters:
			</Typography>

			<Chip
				label={formatTypeChipLabel("Date", [dateRangeLabel])}
				size="small"
				variant="outlined"
				color="primary"
				onDelete={disabled ? undefined : onClearDate}
				sx={chipSx}
				data-testid="orders-date-filter-chip"
			/>

			{warehouseNameLabels.length > 0 && (
				<Chip
					label={formatTypeChipLabel("Warehouse Name", warehouseNameLabels)}
					size="small"
					variant="outlined"
					color="primary"
					onDelete={disabled ? undefined : onClearWarehouseNames}
					sx={chipSx}
					data-testid="orders-warehouse-names-filter-chip"
				/>
			)}

			{warehouseStatusLabels.length > 0 && (
				<Chip
					label={formatTypeChipLabel("Warehouse Status", warehouseStatusLabels)}
					size="small"
					variant="outlined"
					color="primary"
					onDelete={disabled ? undefined : onClearWarehouseStatuses}
					sx={chipSx}
					data-testid="orders-warehouse-statuses-filter-chip"
				/>
			)}

			{courierStatusLabels.length > 0 && (
				<Chip
					label={formatTypeChipLabel("Courier Status", courierStatusLabels)}
					size="small"
					variant="outlined"
					color="primary"
					onDelete={disabled ? undefined : onClearCourierStatuses}
					sx={chipSx}
					data-testid="orders-courier-statuses-filter-chip"
				/>
			)}

			{pastSLA && (
				<Chip
					label={formatTypeChipLabel("Delivery SLA", ["Exceeds Delivery SLA"])}
					size="small"
					variant="outlined"
					color="primary"
					onDelete={disabled ? undefined : onClearPastSLA}
					sx={chipSx}
					data-testid="orders-past-sla-filter-chip"
				/>
			)}

			<Button
				size="small"
				variant="outlined"
				color="secondary"
				onClick={onClearAll}
				disabled={clearAllDisabled || disabled}
				sx={{ ml: 0.5, mb: 0.5, textTransform: "none" }}
				data-testid="orders-clear-all-filters"
			>
				Clear all
			</Button>
		</Box>
	);
};
